import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  IconButton,
  Badge,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  HelpOutlineOutlined,
  AssessmentOutlined,
  AccountTree,
  ExitToAppOutlined,
  AccountCircleOutlined,
  ChevronLeft,
  ChevronRight,
  AdminPanelSettings,
  Campaign,
  Psychology,
} from '@mui/icons-material';
import dashboardIcon from '../assets/icons/dashboardIcon.svg';
import connectionIcon from '../assets/icons/connectionIcon.svg';
import myprojectIcon from '../assets/icons/myprojectIcon.svg';
import settingIcon from '../assets/icons/settingIcon.svg';
import dataverseLogo from '../../Images/dataverse_logo.png';
import templateIcon from '../../Images/Template.png.png';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useMemo, useEffect, useState } from 'react';
import useLogout from '../../hooks/useLogout';
import { getAnnouncements } from '../../apis/Announcement';
import { NotificationPopover } from '../../components/ui/popover';
import { useTrialStatus } from '../../hooks/useTrialStatus';

const EXPANDED_WIDTH = 190;
const COLLAPSED_WIDTH = 64;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = useLogout();
  const [collapsed, setCollapsed] = useState(false);
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [announcementAnchor, setAnnouncementAnchor] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [profileAnchor, setProfileAnchor] = useState(null);

  const username = Cookies.get('username')?.trim() || 'Guest';
  const dispUserName = username.length > 12 ? username.slice(0, 12) + '...' : username;

  const isActive = (path) => location.pathname === path;

  const rawRole =
    Cookies.get('Role_id') ??
    Cookies.get('role_id') ;

  const isAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 2 || n === 1;
  }, [rawRole]);

  const isSuperAdmin = useMemo(() => {
  const n = Number(rawRole);
  return !Number.isNaN(n) && n === 1;
}, [rawRole]);

  const roleName = useMemo(() => {
    const n = Number(rawRole);
    if (Number.isNaN(n)) return 'User';
    switch (n) {
      case 1:
        return 'Super Admin';
      case 2:
        return 'Admin';
      case 3:
        return 'User';
      default:
        return 'User';
    }
  }, [rawRole]);

  const isUser=useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 3;
  })

  const menu = useMemo(() => {
  const base = [
    { label: 'Dashboard', icon: <img src={dashboardIcon} alt="Dashboard" style={{ width: 20, height: 20 }} />, path: '/userdashboard' },
    { label: 'Connections', icon: <img src={connectionIcon} alt="Connections" style={{ width: 20, height: 20 }} />, path: '/myconnections' },
    { label: 'Workspace', icon: <img src={myprojectIcon} alt="Workspace" style={{ width: 20, height: 20 }} />, path: '/recentworkflows' },
    { label: 'Templates', icon: <img src={templateIcon} alt="Pre-Made Templates" style={{ width: 20, height: 20 }} />, path: '/pre-made-templates' },
    { label: 'Reports', icon: <AssessmentOutlined />, path: '/reports' },
    { label: 'Pipeline Runs', icon: <AccountTree />, path: '/pipeline' },
    { label: 'NLP search', icon: <Psychology />, path: '/natural-language-search' },
  ];

  if (isAdmin) {
    const adminPath = isSuperAdmin
      ? '/admin/organizations'  
      : '/admin/user';          

    base.push({
      label: 'Admin',
      icon: <AdminPanelSettings />,
      path: adminPath,
    });
  } else if (isUser) {
    base.push({ label: 'Settings', icon: <img src={settingIcon} alt="Settings" style={{ width: 20, height: 20 }} />, path: '/settings' });
  }

  base.push({ 
    label: 'Announcement', 
    icon: (
      <Badge 
        badgeContent={announcementCount > 0 ? announcementCount : 0} 
        color="error"
        max={99}
        invisible={announcementCount === 0}
      >
        <Campaign />
      </Badge>
    ), 
    path: null,
    isAnnouncement: true,
  });
  base.push({ label: 'Help', icon: <HelpOutlineOutlined />, path: '/help' });

  return base;
}, [isAdmin, isSuperAdmin, isUser, announcementCount]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await getAnnouncements({ page: 1, per_page: 100 });
        const raw = Array.isArray(data?.announcements) ? data.announcements : [];
        
        const active = raw.filter((a) => (a?.is_active ?? true) === true);
        
        if (mounted) {
          setAnnouncementCount(active.length);
          setAnnouncements(active.map((a) => ({
            id: a.announcement_id,
            title: String(a.title ?? '').trim(),
            description: String(a.description ?? '').trim(),
            scheduled_datetime: a.scheduled_datetime,
            is_read: false,
          })));
        }
      } catch (error) {
        console.error('[Sidebar] Error fetching announcements:', error);
        if (mounted) {
          setAnnouncementCount(0);
          setAnnouncements([]);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onAdminRoute = location.pathname.startsWith('/admin');
    if (onAdminRoute && !isAdmin) {
      navigate('/userdashboard', { replace: true });
    }
  }, [location.pathname, isAdmin, navigate]);

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleAnnouncementClose = () => {
    setAnnouncementAnchor(null);
  };

  const openAnnouncement = Boolean(announcementAnchor);
  const openProfile = Boolean(profileAnchor);
  const toggleCollapse = () => setCollapsed(!collapsed);
  const { trialStatus, isOnTrial, isPaid, daysRemaining } = useTrialStatus();

  const handleProfileClick = (event) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchor(null);
  };

  return (
    <Drawer
      variant="permanent"
      data-tour="sidebar"
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        flexShrink: 0,
        transition: 'width 0.3s ease',
        zIndex: 1300,
        '& .MuiDrawer-paper': {
          width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
          height: '100vh',
          background: '#0F172A',
          p: collapsed ? 1 : 1.25,
          backdropFilter: 'blur(8px)',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          overflowX: 'hidden',
          overflowY: 'auto',
          color: '#fff',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease, padding 0.3s ease',
          zIndex: 1300,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: collapsed ? 1 : 1.5,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          minHeight: 60,
          overflow: 'visible',
        }}
      >
        <img
          src={dataverseLogo}
          alt="Dataverse Logo"
          style={{
            width: collapsed ? 32 : 50,
            height: 'auto',
            objectFit: 'contain',
            imageRendering: 'auto',
            WebkitImageRendering: '-webkit-optimize-contrast',
            backfaceVisibility: 'hidden',
            transform: 'translateZ(0)',
          }}
        />
        <IconButton
          onClick={toggleCollapse}
          sx={{
            position: 'fixed',
            left: (collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH) - 12,
            top: 16,
            color: '#9CA3AF',
            backgroundColor: '#0F172A',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            width: 24,
            height: 24,
            zIndex: 1500,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: '#fff',
            },
            transition: 'all 0.2s ease',
            '& svg': {
              fontSize: '20px',
            },
          }}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>

      <List sx={{ flex: 1, mt: 1 }}>
        {menu.map(({ label, icon, path, isAnnouncement }) => {
          const active = path ? isActive(path) : false;
          return (
            <ListItem
              key={label}
              button
              onClick={(e) => {
                if (isAnnouncement) {
                  setAnnouncementAnchor(e.currentTarget);
                } else if (path) {
                  handleMenuClick(path);
                }
              }}
              sx={{
                my: 0.5,
                py: 0.8,
                px: collapsed ? 0.5 : 0.8,
                borderRadius: active && !collapsed ? '8px 0 0 8px' : '4px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                background: active && !collapsed ? '#3D936299' : 'transparent',
                '&:hover': {
                  cursor: 'pointer',
                  background: active && !collapsed 
                    ? '#3D936299' 
                    : 'rgba(255, 255, 255, 0.1)',
                  '& .MuiListItemIcon-root, & .MuiTypography-root': {
                    color: active ? '#fff' : '#fff',
                  },
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <ListItemIcon
                sx={{
                  color: active ? '#fff' : '#9CA3AF',
                  minWidth: collapsed ? 32 : 32,
                  mr: collapsed ? 0 : 2,
                  backgroundColor: active && collapsed ? '#3D936299' : 'transparent',
                  borderRadius: active && collapsed ? '50%' : active && !collapsed ? '8px 0 0 8px' : '0',
                  width: active && collapsed ? 32 : 'auto',
                  height: active && collapsed ? 32 : 'auto',
                  padding: active && collapsed ? '6px' : '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease-in-out',
                  '& img': {
                    filter: active ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(60%)',
                    transition: 'filter 0.3s ease-in-out',
                  },
                }}
              >
                {icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={
                    <Typography 
                      fontSize="0.875rem" 
                      fontWeight={600} 
                      color={active ? '#fff' : '#9CA3AF'}
                      sx={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                    >
                      {label}
                    </Typography>
                  }
                />
              )}
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(255, 255, 255, 0.1)', pt: 1, pb: 1 }}>
        {!collapsed && (
          <Box
            onClick={handleProfileClick}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              py: 1.5,
              px: 1,
              gap: 1.5,
              cursor: 'pointer',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255, 255, 255, 0.2)', mt: 0.5 }}>
              <AccountCircleOutlined sx={{ fontSize: '1.2rem' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ 
                  color: '#fff', 
                  fontWeight: 600, 
                  fontSize: '0.875rem',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
                title={username}
                noWrap
              >
                {dispUserName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#9CA3AF',
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  display: 'block',
                  mt: 0.25,
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                {roleName}
              </Typography>
              {trialStatus && (
                <Box sx={{ mt: 0.75 }}>
                  {isPaid ? (
                    <Chip
                      label="Paid Plan"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(16, 185, 129, 0.15)',
                        color: '#10B981',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: '20px',
                        maxWidth: '100%',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        '& .MuiChip-label': {
                          overflow: 'visible',
                          whiteSpace: 'normal',
                        },
                      }}
                    />
                  ) : isOnTrial ? (
                    <Chip
                      label={
                        daysRemaining !== null && daysRemaining !== undefined
                          ? daysRemaining === 0
                            ? 'Trial Expired'
                            : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`
                          : 'Trial Active'
                      }
                      size="small"
                      sx={{
                        bgcolor: daysRemaining === 0 
                          ? 'rgba(239, 68, 68, 0.15)' 
                          : 'rgba(246, 213, 74, 0.15)',
                        color: daysRemaining === 0 ? '#EF4444' : '#F6D54A',
                        border: `1px solid ${daysRemaining === 0 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(246, 213, 74, 0.3)'}`,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: '20px',
                        maxWidth: '100%',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        '& .MuiChip-label': {
                          overflow: 'visible',
                          whiteSpace: 'normal',
                        },
                      }}
                    />
                  ) : null}
                </Box>
              )}
            </Box>
          </Box>
        )}
        
        {collapsed && (
          <Box
            onClick={handleProfileClick}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1.5,
              cursor: 'pointer',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
              <AccountCircleOutlined sx={{ fontSize: '1.2rem' }} />
            </Avatar>
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={profileAnchor}
        open={openProfile}
        onClose={handleProfileClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 150,
            bgcolor: '#1F2937',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            '& .MuiMenuItem-root': {
              color: '#fff',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => { handleLogout(); handleProfileClose(); }}>
          <ListItemIcon sx={{ color: '#EF4444', minWidth: 36 }}>
            <ExitToAppOutlined fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: '#EF4444' }}>Logout</Typography>
        </MenuItem>
      </Menu>

      <NotificationPopover
        open={openAnnouncement}
        anchorEl={announcementAnchor}
        onClose={handleAnnouncementClose}
        announcements={announcements}
        onMarkAsRead={(announcement) => {
          console.log('Mark as read:', announcement);
        }}
        onDelete={(announcement) => {
          console.log('Delete:', announcement);
        }}
        onEnablePushNotifications={() => {
          console.log('Enable push notifications');
        }}
      />
    </Drawer>
  );
};

export default Sidebar;
